import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  uploadImageDetailsToCloudinary,
  uploadVideoInChunksToCloudinary,
  MAX_IMAGE_SIZE_BYTES,
  MAX_VIDEO_SIZE_BYTES,
} from "../../utils/cloudinaryConfig";
import { newVideo } from "../../api/video";
import {
  MdOutlineVideoCall,
  MdOutlineCloudUpload,
  MdOutlineImage,
  MdOutlineTitle,
  MdOutlineDescription,
  MdOutlineLocalOffer,
  MdClose,
  MdCheckCircle,
} from "react-icons/md";
import { RiSparklingFill, RiArrowRightLine } from "react-icons/ri";
import { BiPlayCircle } from "react-icons/bi";

export default function NewVideo() {
  const navigate = useNavigate();
  const currUser = useSelector((state) => state.auth.user);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const authChecked = useSelector((state) => state.auth.authChecked);

  const [videoDetails, setVideoDetails] = useState({
    title: "",
    description: "",
    tags: [],
    newTag: "",
    photo: null,
    video: null,
    visibility: "public",
    status: "ready",
    duration: 0,
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Chunked Upload Progress State
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState(""); // "thumbnail" | "video" | "metadata" | "complete"
  const [uploadProgress, setUploadProgress] = useState({
    percent: 0,
    currentChunk: 0,
    totalChunks: 0,
    status: "",
  });

  useEffect(() => {
    if (authChecked && !isLoggedIn) {
      toast.info("Please log in to upload videos");
      navigate("/login");
    }
  }, [authChecked, isLoggedIn, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVideoDetails((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleAddTag = (e) => {
    if (e) e.preventDefault();
    const tag = videoDetails.newTag.trim();
    if (tag && !videoDetails.tags.includes(tag)) {
      setVideoDetails((prev) => ({
        ...prev,
        tags: [...prev.tags, tag],
        newTag: "",
      }));
      if (validationErrors.tags) {
        setValidationErrors((prev) => ({ ...prev, tags: "" }));
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setVideoDetails((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tagToRemove),
    }));
  };

  const handleKeyDownTag = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        setValidationErrors((prev) => ({
          ...prev,
          photo: `Thumbnail (${sizeMB} MB) exceeds the 10 MB Cloudinary limit.`,
        }));
        toast.error(`Image (${sizeMB} MB) exceeds Cloudinary 10 MB limit`);
        return;
      }
      setVideoDetails((prev) => ({ ...prev, photo: file }));
      setPhotoPreview(URL.createObjectURL(file));
      if (validationErrors.photo) {
        setValidationErrors((prev) => ({ ...prev, photo: "" }));
      }
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_VIDEO_SIZE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        setValidationErrors((prev) => ({
          ...prev,
          video: `Video (${sizeMB} MB) exceeds Cloudinary's 100 MB free plan limit.`,
        }));
        toast.error(`Video (${sizeMB} MB) exceeds Cloudinary 100 MB limit`);
        return;
      }

      const url = URL.createObjectURL(file);
      setVideoPreview(url);
      const tempVideo = document.createElement("video");
      tempVideo.preload = "metadata";
      tempVideo.onloadedmetadata = () => {
        const dur = Math.round(tempVideo.duration || 0);
        setVideoDetails((prev) => ({
          ...prev,
          video: file,
          duration: dur,
        }));
      };
      tempVideo.src = url;

      if (validationErrors.video) {
        setValidationErrors((prev) => ({ ...prev, video: "" }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!videoDetails.title.trim()) {
      errors.title = "Video title is required";
    }

    if (!videoDetails.description.trim()) {
      errors.description = "Description is required";
    }

    if (videoDetails.tags.length === 0) {
      errors.tags = "Add at least one tag";
    }

    if (!videoDetails.video) {
      errors.video = "Please select a video file to upload";
    } else if (videoDetails.video.size > MAX_VIDEO_SIZE_BYTES) {
      errors.video = "Video file must be under 100 MB (Cloudinary Free Plan)";
    }

    if (videoDetails.photo && videoDetails.photo.size > MAX_IMAGE_SIZE_BYTES) {
      errors.photo = "Thumbnail photo must be under 10 MB";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;

    setIsUploading(true);
    setUploadStage("thumbnail");
    setUploadProgress({
      percent: 5,
      currentChunk: 0,
      totalChunks: 0,
      status: "Starting upload process...",
    });

    try {
      let imgUrl = "";
      let imgPublicId = "";

      // Step 1: Upload Thumbnail Image directly to Cloudinary (if provided, max 10MB)
      if (videoDetails.photo) {
        setUploadStage("thumbnail");
        setUploadProgress({
          percent: 10,
          currentChunk: 0,
          totalChunks: 0,
          status: "Uploading thumbnail photo to Cloudinary...",
        });
        try {
          const imgResult = await uploadImageDetailsToCloudinary(videoDetails.photo);
          imgUrl = imgResult.secure_url;
          imgPublicId = imgResult.public_id;
        } catch (imgError) {
          console.warn("Direct thumbnail upload failed, continuing with fallback:", imgError);
        }
      }

      // Step 2: Upload Video directly to Cloudinary using Request Chunking (6MB chunks, max 100MB)
      setUploadStage("video");
      const videoResult = await uploadVideoInChunksToCloudinary(videoDetails.video, {
        chunkSize: 6 * 1024 * 1024,
        onProgress: (progress) => {
          setUploadProgress({
            percent: Math.min(progress.percent, 95),
            currentChunk: progress.currentChunk,
            totalChunks: progress.totalChunks,
            status: progress.status,
          });
        },
      });

      // If thumbnail wasn't supplied, fallback to Cloudinary generated video poster
      if (!imgUrl && videoResult.secure_url) {
        imgUrl = videoResult.secure_url.replace(/\.[^/.]+$/, ".jpg");
      }

      // Step 3: Send lightweight metadata payload (<1KB) to backend (instant JSON save)
      setUploadStage("metadata");
      setUploadProgress({
        percent: 98,
        currentChunk: uploadProgress.totalChunks,
        totalChunks: uploadProgress.totalChunks,
        status: "Saving video metadata to WIDEO...",
      });

      const videoPayload = {
        user: {
          id: currUser?.id,
          name: currUser?.name,
          imgUrl: currUser?.profileImageUrl || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&auto=format&fit=crop",
        },
        title: videoDetails.title.trim(),
        description: videoDetails.description.trim(),
        imgUrl: imgUrl || "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&auto=format&fit=crop",
        videoUrl: videoResult.secure_url,
        tags: videoDetails.tags,
        visibility: videoDetails.visibility,
        status: videoDetails.status,
        duration: videoResult.duration || videoDetails.duration || 0,
        imgPublicId: imgPublicId || "",
        videoPublicId: videoResult.public_id || "",
      };

      const savedVideo = await newVideo(videoPayload);

      setUploadStage("complete");
      setUploadProgress({
        percent: 100,
        currentChunk: uploadProgress.totalChunks,
        totalChunks: uploadProgress.totalChunks,
        status: "Video successfully uploaded and published!",
      });

      toast.success("Video uploaded successfully!");
      setTimeout(() => {
        const videoId = savedVideo?.data?._id || savedVideo?._id;
        navigate(videoId ? `/video/${videoId}` : "/yourvideos");
      }, 1200);
    } catch (error) {
      console.error("Error during chunked upload:", error);
      const errorMsg = error?.response?.data?.message || error?.response?.data || error?.message || "Failed to upload video";
      toast.error(typeof errorMsg === "string" ? errorMsg : "Failed to upload video");
      setIsUploading(false);
    }
  };

  if (!authChecked || !isLoggedIn) {
    return null;
  }

  return (
    <div className="w-full min-h-[calc(100vh-4rem)] container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-5xl">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-2">
            <RiSparklingFill className="w-3 h-3" />
            <span>Direct Chunked Upload</span>
          </div>
          <h1 className="gradient-text text-2xl sm:text-3xl font-extrabold tracking-tight">
            Publish New Video
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-1">
            Fast client-side chunked streaming directly to Cloud CDN (Up to 100 MB free tier).
          </p>
        </div>
        <Link
          to="/yourvideos"
          className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition"
        >
          Cancel & Return
        </Link>
      </div>

      {/* Upload Progress Modal / Overlay */}
      {isUploading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md px-4">
          <div className="glass-panel bg-[#111a2e] border border-cyan-500/40 rounded-2xl p-8 max-w-lg w-full shadow-2xl text-center flex flex-col items-center">
            {uploadStage === "complete" ? (
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 mb-4 animate-bounce">
                <MdCheckCircle className="w-8 h-8" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-cyan-400 mb-4 animate-pulse">
                <MdOutlineCloudUpload className="w-8 h-8" />
              </div>
            )}

            <h3 className="text-lg font-bold text-slate-100 mb-1">
              {uploadStage === "complete" ? "Upload Completed!" : "Uploading Video..."}
            </h3>
            <p className="text-xs text-slate-400 mb-5">{uploadProgress.status}</p>

            {/* Progress Bar */}
            <div className="w-full bg-[#0b132b] rounded-full h-3.5 border border-slate-700 overflow-hidden mb-3 p-0.5">
              <div
                className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-all duration-300 shadow-md shadow-cyan-500/50"
                style={{ width: `${uploadProgress.percent}%` }}
              ></div>
            </div>

            <div className="flex justify-between w-full text-xs text-slate-400 font-semibold mb-6">
              <span>{uploadProgress.percent}% Complete</span>
              {uploadProgress.totalChunks > 0 && (
                <span>
                  Chunk {uploadProgress.currentChunk} of {uploadProgress.totalChunks}
                </span>
              )}
            </div>

            {uploadStage === "complete" && (
              <div className="text-xs text-emerald-400 font-medium">
                Redirecting to your new video player...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Upload Form */}
      <form
        onSubmit={handleSubmit}
        className="glass-panel bg-[#111a2e]/90 border border-slate-700/80 rounded-2xl p-6 sm:p-8 shadow-2xl flex flex-col gap-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Metadata */}
          <div className="lg:col-span-7 flex flex-col gap-5">
            {/* Title */}
            <div>
              <label
                htmlFor="title"
                className="block text-slate-300 font-semibold mb-1.5 text-xs uppercase tracking-wider"
              >
                Video Title <span className="text-cyan-400">*</span>
              </label>
              <div className="relative flex items-center">
                <MdOutlineTitle className="absolute left-3.5 w-4 h-4 text-slate-500 pointer-events-none" />
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={videoDetails.title}
                  onChange={handleInputChange}
                  placeholder="e.g. Building Modern Web Applications with React"
                  className={`w-full bg-[#0b132b] text-slate-100 placeholder-slate-500 rounded-xl pl-10 pr-4 py-2.5 border text-sm font-medium focus:outline-none transition ${
                    validationErrors.title
                      ? "border-rose-500 focus:border-rose-400 focus:ring-1 focus:ring-rose-400/50"
                      : "border-slate-700/80 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50"
                  }`}
                />
              </div>
              {validationErrors.title && (
                <p className="text-rose-400 text-xs mt-1 font-medium">{validationErrors.title}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-slate-300 font-semibold mb-1.5 text-xs uppercase tracking-wider"
              >
                Description <span className="text-cyan-400">*</span>
              </label>
              <div className="relative">
                <textarea
                  id="description"
                  name="description"
                  rows="4"
                  value={videoDetails.description}
                  onChange={handleInputChange}
                  placeholder="Tell viewers what your video is about, links, topics covered, and chapters..."
                  className={`w-full bg-[#0b132b] text-slate-100 placeholder-slate-500 rounded-xl p-4 border text-sm font-medium focus:outline-none transition resize-y min-h-[100px] ${
                    validationErrors.description
                      ? "border-rose-500 focus:border-rose-400 focus:ring-1 focus:ring-rose-400/50"
                      : "border-slate-700/80 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50"
                  }`}
                ></textarea>
              </div>
              {validationErrors.description && (
                <p className="text-rose-400 text-xs mt-1 font-medium">{validationErrors.description}</p>
              )}
            </div>

            {/* Tags Input */}
            <div>
              <label
                htmlFor="newTag"
                className="block text-slate-300 font-semibold mb-1.5 text-xs uppercase tracking-wider"
              >
                Tags <span className="text-cyan-400">*</span>
              </label>
              <div className="flex gap-2 mb-2.5">
                <div className="relative flex-1 flex items-center">
                  <MdOutlineLocalOffer className="absolute left-3.5 w-4 h-4 text-slate-500 pointer-events-none" />
                  <input
                    type="text"
                    id="newTag"
                    name="newTag"
                    value={videoDetails.newTag}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDownTag}
                    placeholder="e.g. javascript, tutorial, coding (press enter)"
                    className="w-full bg-[#0b132b] text-slate-100 placeholder-slate-500 rounded-xl pl-10 pr-4 py-2.5 border border-slate-700/80 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 text-sm font-medium focus:outline-none transition"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/30 transition cursor-pointer"
                >
                  Add Tag
                </button>
              </div>

              {/* Tag Chips */}
              <div className="flex flex-wrap gap-2">
                {videoDetails.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-medium"
                  >
                    #{tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-rose-400 transition"
                      aria-label={`Remove tag ${tag}`}
                    >
                      <MdClose className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
              {validationErrors.tags && (
                <p className="text-rose-400 text-xs mt-1 font-medium">{validationErrors.tags}</p>
              )}
            </div>

            {/* Visibility & Status Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <label
                  htmlFor="visibility"
                  className="block text-slate-300 font-semibold mb-1.5 text-xs uppercase tracking-wider"
                >
                  Visibility
                </label>
                <select
                  id="visibility"
                  name="visibility"
                  value={videoDetails.visibility}
                  onChange={handleInputChange}
                  className="w-full bg-[#0b132b] text-slate-100 rounded-xl px-4 py-2.5 border border-slate-700/80 focus:border-cyan-400 focus:outline-none text-sm font-medium cursor-pointer"
                >
                  <option value="public">Public (Visible to everyone)</option>
                  <option value="private">Private (Only you can view)</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="status"
                  className="block text-slate-300 font-semibold mb-1.5 text-xs uppercase tracking-wider"
                >
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={videoDetails.status}
                  onChange={handleInputChange}
                  className="w-full bg-[#0b132b] text-slate-100 rounded-xl px-4 py-2.5 border border-slate-700/80 focus:border-cyan-400 focus:outline-none text-sm font-medium cursor-pointer"
                >
                  <option value="ready">Ready (Published immediately)</option>
                  <option value="draft">Draft (Save without publishing)</option>
                  <option value="processing">Processing</option>
                </select>
              </div>
            </div>
          </div>

          {/* Right Column: Video & Thumbnail Pickers */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            {/* Video File Picker & Preview */}
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label
                  htmlFor="video"
                  className="block text-slate-300 font-semibold text-xs uppercase tracking-wider"
                >
                  Video File <span className="text-cyan-400">*</span>
                </label>
                <span className="text-[10px] text-cyan-400/90 font-medium">Max 100 MB</span>
              </div>

              <div className="relative">
                <input
                  type="file"
                  id="video"
                  name="video"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  className="block w-full text-xs text-slate-400 file:mr-3 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-cyan-500 file:text-white hover:file:bg-cyan-400 file:cursor-pointer cursor-pointer bg-[#0b132b] border border-slate-700/80 rounded-xl focus:outline-none"
                />
              </div>

              {validationErrors.video && (
                <p className="text-rose-400 text-xs mt-1 font-medium">{validationErrors.video}</p>
              )}

              {/* Video Player Preview */}
              {videoPreview && (
                <div className="mt-3 rounded-xl overflow-hidden border border-slate-700/80 bg-[#0b132b]">
                  <video
                    src={videoPreview}
                    controls
                    className="w-full max-h-48 object-cover"
                  ></video>
                  {videoDetails.duration > 0 && (
                    <div className="p-2.5 bg-slate-900/80 flex items-center justify-between text-xs text-slate-400">
                      <span>Detected Duration:</span>
                      <span className="text-cyan-400 font-bold">{videoDetails.duration}s</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Thumbnail Photo Picker & Preview */}
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label
                  htmlFor="photo"
                  className="block text-slate-300 font-semibold text-xs uppercase tracking-wider"
                >
                  Custom Thumbnail
                </label>
                <span className="text-[10px] text-cyan-400/90 font-medium">Max 10 MB</span>
              </div>

              <input
                type="file"
                id="photo"
                name="photo"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="block w-full text-xs text-slate-400 file:mr-3 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-blue-600 file:text-white hover:file:bg-blue-500 file:cursor-pointer cursor-pointer bg-[#0b132b] border border-slate-700/80 rounded-xl focus:outline-none"
              />

              {validationErrors.photo && (
                <p className="text-rose-400 text-xs mt-1 font-medium">{validationErrors.photo}</p>
              )}

              {photoPreview ? (
                <div className="mt-3 rounded-xl overflow-hidden border border-slate-700/80 bg-[#0b132b] p-1">
                  <img
                    src={photoPreview}
                    alt="Thumbnail Preview"
                    className="w-full max-h-36 object-cover rounded-lg"
                  />
                </div>
              ) : (
                <p className="text-xs text-slate-500 mt-1.5">
                  Optional: If omitted, a poster frame will automatically be extracted from your video.
                </p>
              )}
            </div>

            {/* Chunking & Limits Info Badge */}
            <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20 text-xs text-slate-400 flex items-start gap-3">
              <RiSparklingFill className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
              <div>
                <strong className="text-slate-300 block mb-1">Cloudinary Free Tier Limits</strong>
                <span className="block mb-1">
                  • <strong>Max Video:</strong> 100 MB (Auto-chunked in 6MB pieces)
                </span>
                <span>
                  • <strong>Max Image:</strong> 10 MB (Avatars, banners & thumbnails)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Actions */}
        <div className="pt-4 border-t border-slate-700/80 flex flex-col sm:flex-row items-center justify-end gap-3">
          <Link
            to="/yourvideos"
            className="w-full sm:w-auto px-5 py-3 rounded-xl text-xs font-bold text-slate-400 hover:text-slate-200 text-center transition"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isUploading}
            className="glow-button w-full sm:w-auto px-8 py-3 rounded-xl text-xs font-bold text-white uppercase tracking-wider shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <MdOutlineCloudUpload className="w-4 h-4" />
            <span>Upload & Publish Video</span>
          </button>
        </div>
      </form>
    </div>
  );
}
